/* import __COLOCATED_TEMPLATE__ from './unpublish-modal.hbs'; */
/* RESPONSIBLE TEAM: team-knowledge-foundations */
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import type IntlService from 'ember-intl/services/intl';
import type ArticleContent from 'embercom/models/articles/article-content';
import type ScheduledContentUpdate from 'embercom/models/content-service/scheduled-content-update';
import { ScheduledContentUpdateStatus } from 'embercom/services/scheduled-content-update-service';
import { type ScheduleMode } from 'embercom/lib/knowledge-hub/constants';
import type { ScheduleSelection } from './schedule-picker';

interface Args {
  activeContent: ArticleContent;
  unpublishActiveContent: () => Promise<void>;
  scheduleActiveContentUpdate?: (params: {
    mode: ScheduleMode;
    scheduledDate: string;
    timezone?: string;
  }) => Promise<void>;
  onClose: () => void;
}

interface Signature {
  Args: Args;
}

export default class UnpublishModal extends Component<Signature> {
  @service declare intl: IntlService;

  @tracked schedule: ScheduleSelection;
  @tracked isUnpublishing = false;

  constructor(owner: unknown, args: Args) {
    super(owner, args);
    // Pre-sync with <SchedulePicker>'s initial render so confirming without
    // interaction matches the picker's visible state.
    this.schedule = this.pendingUnpublish ? this.pendingScheduleAsSelection() : { kind: 'now' };
  }

  get pendingUnpublish(): ScheduledContentUpdate | undefined {
    return this.args.activeContent.scheduledContentUpdates?.find(
      (u) =>
        u.status === ScheduledContentUpdateStatus.PENDING && u.contentUpdateType === 'unpublish',
    );
  }

  get canSchedule(): boolean {
    return !!this.args.scheduleActiveContentUpdate;
  }

  get isScheduleMode(): boolean {
    return this.schedule.kind === 'schedule';
  }

  get modalTitle(): string {
    return this.intl.t('knowledge-hub.content-editor.article-content.unpublish-modal.title');
  }

  get confirmButtonLabel(): string {
    return this.intl.t(
      this.isScheduleMode
        ? 'knowledge-hub.content-editor.article-content.unpublish-modal.confirm-schedule'
        : 'knowledge-hub.content-editor.article-content.unpublish-modal.confirm-unpublish',
    );
  }

  get cancelButtonLabel(): string {
    return this.intl.t('knowledge-hub.content-editor.article-content.buttons.cancel');
  }

  private pendingScheduleAsSelection(): ScheduleSelection {
    let pending = this.pendingUnpublish!;
    return {
      kind: 'schedule',
      scheduledDate: pending.changeAt.toISOString(),
      timezone: pending.timezone || '',
    };
  }

  @action onScheduleChange(selection: ScheduleSelection) {
    this.schedule = selection;
  }

  @action async confirm() {
    if (this.isScheduleMode && this.args.scheduleActiveContentUpdate) {
      let { scheduledDate, timezone } = this.schedule as Extract<
        ScheduleSelection,
        { kind: 'schedule' }
      >;
      await this.args.scheduleActiveContentUpdate({
        mode: 'unpublish',
        scheduledDate,
        timezone,
      });
      this.args.onClose();
      return;
    }
    // Existing pending schedule (if any) is cancelled by the service-level
    // cancel-then-unpublish path; no second confirmation needed here.
    this.isUnpublishing = true;
    await this.args.unpublishActiveContent();
    this.isUnpublishing = false;
    this.args.onClose();
  }

  @action cancel() {
    this.args.onClose();
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'KnowledgeHub::ContentEditor::ArticleContent::UnpublishModal': typeof UnpublishModal;
    'knowledge-hub/content-editor/article-content/unpublish-modal': typeof UnpublishModal;
  }
}
